import React from 'react';
import { Link } from 'react-router-dom';
import { FileText, Calculator, BookOpen, DollarSign, ArrowRight, CheckCircle } from 'lucide-react';
import { services } from '../data/mock';

const Home = () => {
  return (
    <div 
      className="relative"
      style={{
        backgroundImage: 'url(https://customer-assets.emergentagent.com/job_taxsmart-11/artifacts/4b6ow0h4_WEBSITE.png)',
        backgroundSize: 'cover',
        backgroundPosition: 'center',
        backgroundAttachment: 'fixed'
      }}
    >
      {/* Overlay bleu */}
      <div className="absolute inset-0 bg-blue-900 opacity-85 z-0"></div>
      
      {/* Contenu de la page */}
      <div className="relative z-10">
      {/* Hero Section */}
      <section className="relative py-20">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center">
            <h1 className="text-5xl md:text-6xl font-bold text-white mb-6" style={{ lineHeight: '1.5' }}>
              Simplifiez votre <span className="text-green-400">fiscalité</span> avec<br />
              <span className="text-blue-200">PROTAXESPLUS</span>
            </h1>
            <p className="text-xl text-gray-100 mb-8 max-w-3xl mx-auto" style={{ 
              fontFamily: "'Inter', 'Segoe UI', 'Roboto', sans-serif",
              fontWeight: '400',
              letterSpacing: '0.3px'
            }}>
              Accompagnement professionnel en fiscalité, comptabilité, tenue de livres et administration de la paie pour une clientèle individuelle et corporative.
            </p>
            
            {/* Slogan */}
            <div className="mb-10">
              <p className="text-2xl md:text-3xl font-bold text-white italic" style={{ 
                fontFamily: 'Georgia, serif',
                textShadow: '3px 3px 6px rgba(0, 0, 0, 0.9)',
                letterSpacing: '0.5px'
              }}>
                Votre tranquillité fiscale, Notre priorité !
              </p>
            </div>
            
            <div className="flex flex-col sm:flex-row gap-4 justify-center">
              <Link
                to="/contact"
                className="inline-flex items-center justify-center px-8 py-4 text-lg font-semibold text-white bg-blue-600 rounded-lg hover:bg-blue-700 transition-all duration-200 shadow-lg hover:shadow-xl"
              >
                Contactez-nous
                <ArrowRight className="ml-2 w-5 h-5" />
              </Link>
              <Link
                to="/services"
                className="inline-flex items-center justify-center px-8 py-4 text-lg font-semibold text-blue-600 bg-white border-2 border-blue-600 rounded-lg hover:bg-blue-50 transition-all duration-200 shadow-lg hover:shadow-xl"
              >
                Nos services
              </Link>
            </div>
          </div>
        </div>
      </section>

      {/* Services Overview */}
      <section className="py-20 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <h2 className="text-4xl font-bold text-gray-900 mb-4">
              Nos services professionnels
            </h2>
            <p className="text-xl text-gray-600 max-w-2xl mx-auto">
              Des solutions complètes adaptées à vos besoins fiscaux et comptables
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-8">
            {services.map((service, index) => {
              const IconComponent = service.icon === 'FileText' ? FileText :
                                   service.icon === 'Calculator' ? Calculator :
                                   service.icon === 'BookOpen' ? BookOpen : DollarSign;
              
              const colors = [
                { bg: 'bg-blue-100', text: 'text-blue-600', border: 'border-blue-600' },
                { bg: 'bg-green-100', text: 'text-green-600', border: 'border-green-600' },
                { bg: 'bg-blue-100', text: 'text-blue-600', border: 'border-blue-600' },
                { bg: 'bg-green-100', text: 'text-green-600', border: 'border-green-600' }
              ];

              return (
                <div
                  key={service.id}
                  className="bg-white border-2 border-gray-200 rounded-xl p-6 hover:border-blue-600 transition-all duration-300 hover:shadow-xl group"
                >
                  <div className={`${colors[index].bg} w-16 h-16 rounded-lg flex items-center justify-center mb-4 group-hover:scale-110 transition-transform duration-300`}>
                    <IconComponent className={`w-8 h-8 ${colors[index].text}`} />
                  </div>
                  <h3 className="text-xl font-bold text-gray-900 mb-3">{service.title}</h3>
                  <p className="text-gray-600 mb-4">{service.description}</p>
                  <Link
                    to="/services"
                    className={`inline-flex items-center ${colors[index].text} font-semibold hover:underline`}
                  >
                    En savoir plus
                    <ArrowRight className="ml-1 w-4 h-4" />
                  </Link>
                </div>
              );
            })}
          </div>
        </div>
      </section>

      {/* Why Choose Us */}
      <section className="py-20 bg-gradient-to-br from-blue-700 via-blue-600 to-blue-500 text-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <h2 className="text-4xl font-bold mb-4">Pourquoi choisir PROTAXESPLUS ?</h2>
            <p className="text-xl text-blue-100 max-w-2xl mx-auto">
              Votre partenaire de confiance pour tous vos besoins fiscaux et comptables
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
            <div className="bg-white rounded-xl p-6 hover:shadow-xl transition-all duration-300">
              <CheckCircle className="w-12 h-12 text-blue-900 mb-4" />
              <h3 className="text-xl font-bold mb-3 text-gray-900">Expertise professionnelle</h3>
              <p className="text-gray-700">
                Équipe qualifiée avec des années d'expérience en fiscalité et comptabilité.
              </p>
            </div>

            <div className="bg-white rounded-xl p-6 hover:shadow-xl transition-all duration-300">
              <CheckCircle className="w-12 h-12 text-blue-900 mb-4" />
              <h3 className="text-xl font-bold mb-3 text-gray-900">Service personnalisé</h3>
              <p className="text-gray-700">
                Solutions adaptées à vos besoins spécifiques, qu'importe votre situation.
              </p>
            </div>

            <div className="bg-white rounded-xl p-6 hover:shadow-xl transition-all duration-300">
              <CheckCircle className="w-12 h-12 text-blue-900 mb-4" />
              <h3 className="text-xl font-bold mb-3 text-gray-900">Prix compétitifs</h3>
              <p className="text-gray-700">
                Tarifs transparents et abordables pour tous types de services fiscaux.
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* Testimonials Section - Google Reviews */}
      <section className="py-20 bg-gray-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-12">
            <h2 className="text-4xl font-bold text-gray-900 mb-4">
              Ce que nos clients disent
            </h2>
            <p className="text-xl text-gray-600 max-w-2xl mx-auto">
              Découvrez les avis de nos clients satisfaits
            </p>
          </div>
          
          {/* Elfsight Google Reviews Widget */}
          <div className="flex justify-center">
            <div className="elfsight-app-25cb62bc-9ef8-4c3c-87a5-9c6e1fcacde1" data-elfsight-app-lazy></div>
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-20 bg-blue-900 text-white">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <h2 className="text-4xl font-bold mb-6">
            Prêt à simplifier vos finances ?
          </h2>
          <p className="text-xl text-blue-100 mb-8">
            Contactez-nous dès aujourd'hui pour une consultation gratuite
          </p>
          <Link
            to="/contact"
            className="inline-flex items-center justify-center px-8 py-4 text-lg font-semibold text-blue-600 bg-white rounded-lg hover:bg-gray-100 transition-all duration-200 shadow-lg hover:shadow-xl"
          >
            Prendre rendez-vous
            <ArrowRight className="ml-2 w-5 h-5" />
          </Link>
        </div>
      </section>
      </div>
    </div>
  );
};

export default Home;
